import { Box, Text } from "@mantine/core"
import { IOrderPlus } from "../../models/Order"
import { dateTimeSecFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import {
  DeliveryStatusBadge, InvoiceStatusBadge, PaymentStatusBadge, PurchaseTypeBadge,
} from "../order/status-badges"
import RenderText from "../ui/render-text"
import { ListColumn } from "./list-table"
import { OrderStatusTag } from "./status-tag"

type T = (key: string) => string

const dash = (v: string | number | null | undefined) => <RenderText text={v as string} />
const date = (v?: string | null) => dash(v ? dateTimeSecFormat(v) : null)

/** Two-line business cell: bold name, monospace code under it, «-» when there is neither. */
const business = (b?: { refCode?: string | null; partner?: { refCode?: string | null } | null } | null) => (
  <Box>
    <Text fz={14} fw={600}>{renderBusinessName(b as never, "") || "-"}</Text>
    <Text fz={14} c="gray.8" ff="monospace">{b?.refCode || "-"}</Text>
  </Box>
)

/**
 * Ord_Purchase default columns: PO №, PO date (response date), type, buyer business, status,
 * delivery date, amount, invoice. The former 24-column table survives as optional columns.
 */
export function purchaseColumns(t: T): ListColumn<IOrderPlus>[] {
  return [
    { id: "poNumber", label: "PO №", sort: "purchaseCode", render: (r) => <Text ff="monospace" fw={700} fz={14} style={{ whiteSpace: "nowrap" }}>{r.purchaseCode || "-"}</Text> },
    { id: "poDate", label: t("poDate"), sort: "respondedDate", render: (r) => date(r.respondedDate) },
    { id: "poType", label: t("poType"), sort: "purchaseType", render: (r) => <PurchaseTypeBadge code={r.purchaseType} /> },
    { id: "buyerBusiness", label: t("buyerBusiness"), sort: "senderBusinessRefCodeProfileName", render: (r) => business(r.senderBusiness) },
    { id: "poStatus", label: t("poStatus"), sort: "orderStatus", render: (r) => <OrderStatusTag code={r.orderStatus} perspective="received" /> },
    { id: "deliveryDate", label: t("deliveryDate"), sort: "deliveryDate", render: (r) => date(r.deliveryDate) },
    { id: "amount", label: t("orderAmount"), sort: "orderAmount", align: "right", render: (r) => <RenderText text={r.orderAmount || 0} isAmount plainAmount fontWeight={700} /> },
    { id: "invoiceNo", label: t("invoiceNo"), sort: "invoiceRefCode", render: (r) => dash(r.invoiceId ? r.invoice?.refCode : null) },
    { id: "buyerPartner", label: t("buyer"), optional: true, sort: "senderBusinessPartnerRefCodeBusinessName", render: (r) => business(r.senderBusiness?.partner as never) },
    { id: "supplierPartner", label: t("supplierPartner"), optional: true, sort: "receiverBusinessPartnerRefCodeBusinessName", render: (r) => business(r.receiverBusiness?.partner as never) },
    { id: "supplierBusiness", label: t("supplierBusiness"), optional: true, sort: "receiverBusinessRefCodeProfileName", render: (r) => business(r.receiverBusiness) },
    {
      id: "deliveryStatus", label: t("deliveryStatus"), optional: true, sort: "deliveryNoteStatus",
      render: (r) => (r.deliveryNote ? <DeliveryStatusBadge code={r.deliveryNote.deliveryNoteStatus} sla={r.deliveryNote.sla as never} /> : dash(null)),
    },
    {
      id: "courier", label: t("deliveryPerson"), optional: true, sort: "deliveryNoteStaffFirstNameEmail",
      render: (r) => (
        <Box>
          <RenderText text={renderName(r.deliveryNote?.staff)} />
          <RenderText text={r.deliveryNote?.staff?.email} />
        </Box>
      ),
    },
    { id: "branch", label: t("receiverBranch"), optional: true, sort: "receiverBranchName", render: (r) => dash(r.receiverBranch?.name) },
    { id: "receiverStaff", label: t("receiverStaff"), optional: true, sort: "receiverStaffLastNameFirstName", render: (r) => dash(renderName(r.receiverStaff)) },
    { id: "paidAmount", label: t("paidAmount"), optional: true, sort: "paidAmount", align: "right", render: (r) => <RenderText text={r.paidAmount || 0} isAmount plainAmount fontWeight={600} /> },
    { id: "amountToPay", label: t("remainingAmount"), optional: true, sort: "amountToPay", align: "right", render: (r) => <RenderText text={r.amountToPay || 0} isAmount plainAmount fontWeight={600} /> },
    { id: "paymentStatus", label: t("paymentStatus"), optional: true, sort: "paymentStatus", render: (r) => (r.invoiceId ? <PaymentStatusBadge code={r.invoice?.paymentStatus} /> : dash(null)) },
    { id: "invoiceStatus", label: t("invoiceStatus"), optional: true, sort: "invoiceStatus", render: (r) => (r.invoiceId ? <InvoiceStatusBadge code={r.invoice?.invoiceStatus} /> : dash(null)) },
    { id: "paymentTerm", label: t("paymentTerm"), optional: true, sort: "paymentTermDescription", render: (r) => dash(r.paymentTerm?.description) },
    { id: "orderedBy", label: t("poCreatedBy"), optional: true, sort: "orderedUserLastNameFirstName", render: (r) => dash(renderName(r.orderedUser)) },
    { id: "reviewedBy", label: t("poReviewed"), optional: true, render: (r) => dash(renderName(r.orderStatus === "APPROVED" || r.orderStatus === "AUTHORIZED" ? r.respondedUser : r.reviewedUser)) },
  ]
}
